import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Account } from '@wb/core/interfaces/account';
import { Inventory } from '@wb/core/interfaces/inventory';
import {
  InsertVisitorGQL,
  InsertVisitorNameGQL,
  InsertVisitorResponse,
  NetdeskerPiiGQL,
  SetupLeadGQL,
  SetupLeadResponse,
} from '@wb/core/schema/mutation-definitions/accounts.graphql';
import { AccountService } from '@wb/core/services/account.service';
import { InventoryService } from '@wb/core/services/inventory.service';
import { PhoneValidation } from '@wb/validators/phoneValidator';
import { Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-build-deal-dialog',
  templateUrl: './build-deal-dialog.component.html',
  styleUrls: ['./build-deal-dialog.component.sass'],
})
export class BuildDealDialogComponent implements OnInit, OnDestroy {
  @Output() save = new EventEmitter();

  @Output() cancel = new EventEmitter();

  public formGroup: FormGroup = new FormGroup({
    firstName: new FormControl(null, [Validators.required]),
    lastName: new FormControl(null, [Validators.required]),
    emailAddress: new FormControl(null, [Validators.required, Validators.email]),
    phoneNumber: new FormControl(null, [Validators.required, PhoneValidation()]),
  });

  public activeInventory: Inventory | undefined;

  public activeAccountUid?: string;

  private netdeskerPii: any[] = [];

  private destroyed$: Subject<void> = new Subject<void>();

  constructor(
    private readonly dialogRef: MatDialogRef<BuildDealDialogComponent>,
    private readonly router: Router,
    private readonly setupLeadGQL: SetupLeadGQL,
    private readonly netdeskerPiiGQL: NetdeskerPiiGQL,
    private readonly insertVisitorGQL: InsertVisitorGQL,
    private readonly insertVisitorNameGQL: InsertVisitorNameGQL,
    private readonly accountService: AccountService,
    private readonly inventoryService: InventoryService,
  ) {}

  ngOnInit(): void {
    this.accountService.selectedAccount
      .pipe(
        filter((account) => !!account),
        takeUntil(this.destroyed$),
      )
      .subscribe((account) => {
        this.activeAccountUid = (account as Account).uid;
        if (account) {
          this.netdeskerPii = [
            { key: 'city', value: account.city },
            { key: 'county', value: account.county },
            { key: 'state', value: account.state },
            { key: 'zipcode', value: account.postalcode },
          ];
        }
      });

    this.inventoryService.selectedInventory
      .pipe(takeUntil(this.destroyed$))
      .subscribe((inventory: Inventory | undefined) => {
        if (inventory) {
          this.activeInventory = inventory;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Save vehicle and form data as a new lead
   */
  async onSave() {
    let visitorUid: any = await this.insertVisitor();

    if (visitorUid !== '') {
      this.insertVisitorName(visitorUid);

      let lead: String = await this.setupLead(visitorUid);

      // Route to the deal editor
      if (lead !== '') {
        this.router.navigate(['/deal-editor', this.activeAccountUid, lead, visitorUid], {
          queryParams: { forceIntro: true, buildDeal: true },
        });
        this.dialogRef.close();
      }
    }
  }

  /**
   * Inserts the name of the visitor by UID
   * @param visitorUid
   */
  async insertVisitorName(visitorUid: String) {
    this.insertVisitorNameGQL
      .mutate(
        {
          visitor_uid: visitorUid,
          input: {
            first_name: this.formGroup.value.firstName,
            last_name: this.formGroup.value.lastName,
            email_address: this.formGroup.value.emailAddress,
            phone_number: this.formGroup.value.phoneNumber,
          },
        },
        {
          fetchPolicy: 'no-cache',
        },
      )
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => {},
        error: (err: HttpErrorResponse) => {
          console.error(err);
        },
      });
  }

  /**
   * Inserts a new visitor for the lead
   * @returns
   */
  async insertVisitor(): Promise<String> {
    return new Promise<String>((resolve, reject) => {
      this.insertVisitorGQL
        .mutate(
          {
            input: {},
          },
          {
            fetchPolicy: 'no-cache',
          },
        )
        .pipe(
          map((result) => result.data),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: (response: InsertVisitorResponse | null | undefined) => {
            if (response?.insertVisitor.success) {
              resolve(response.insertVisitor.visitor.uid);
            }
          },
          error: (err: HttpErrorResponse) => {
            if (err) {
              console.error(err);
            }

            reject();
          },
        });
    });
  }

  /**
   * Sets up a new lead
   * @param visitorUid
   * @returns
   */
  async setupLead(visitorUid: String): Promise<String> {
    return new Promise<String>((resolve, reject) => {
      this.setupLeadGQL
        .mutate(
          {
            input: {
              accountUid: this.activeAccountUid,
              userUid: visitorUid,
              item: { vin: this.activeInventory?.vin },
              leadType: 'showroom',
            },
          },
          {
            fetchPolicy: 'no-cache',
          },
        )
        .pipe(
          map((result) => result.data),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: (response: SetupLeadResponse | null | undefined) => {
            if (response && response?.leadSetup?.body?.lead?.visitor?.uid && this.netdeskerPii) {
              this.netdeskerPiiGQL
                .mutate({
                  visitor_uid: response?.leadSetup?.body?.lead?.visitor?.uid,
                  input: this.netdeskerPii,
                })
                .pipe(take(1))
                .subscribe(() => {
                  if (response) {
                    resolve(response.leadSetup.body.lead.uid);
                  }
                });
            }
          },
          error: (err: HttpErrorResponse) => {
            if (err) {
              console.error(err);
            }

            reject('');
          },
        });
    });
  }
}
